import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  useTestBenchStore,
  type TestBenchModel,
  type TestBenchResult,
  type TestBenchSummary,
} from '../stores/testBenchStore';
import { useConfigStore } from '../stores/configStore';
import { runCost, fmtCost } from '../utils/benchCost';
import './TestBenchPage.css';

function modelKey(m: TestBenchModel): string {
  return m.id || m.displayName;
}

export default function TestBenchPage() {
  const {
    contentTypes,
    flaggedContentTypes,
    results,
    summary,
    total,
    models,
    loadingResults,
    loadingModels,
    running,
    runProgress,
    error,
    fetchContentTypes,
    fetchModels,
    fetchResults,
    runBench,
    stopRun,
    clearError,
  } = useTestBenchStore();

  const { modelPricing, fetchModelPricing } = useConfigStore();

  const { stagedWords, addStagedWord, removeStagedWord } = useTestBenchStore();

  const [contentType, setContentType] = useState<string>('');
  const [selectedModels, setSelectedModels] = useState<Set<string>>(new Set());
  const [newWord, setNewWord] = useState<string>('');
  const [modelsOpen, setModelsOpen] = useState<boolean>(false);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<boolean>(false);

  useEffect(() => {
    fetchContentTypes();
    fetchModels();
    fetchModelPricing();
  }, [fetchContentTypes, fetchModels, fetchModelPricing]);

  // Per-model cost rollup across the (content-type-scoped) stored results:
  // total spent and average per run, from token usage × registry pricing.
  const costByModel = useMemo(() => {
    const acc: Record<string, { total: number; count: number }> = {};
    for (const r of results) {
      const c = runCost(r, modelPricing);
      if (c == null) continue;
      if (!acc[r.model]) acc[r.model] = { total: 0, count: 0 };
      acc[r.model].total += c;
      acc[r.model].count += 1;
    }
    return acc;
  }, [results, modelPricing]);

  // Default the content type to the first available, once loaded.
  useEffect(() => {
    if (!contentType && contentTypes.length > 0) setContentType(contentTypes[0].key);
  }, [contentType, contentTypes]);

  // Scope the stored results to the selected content type.
  useEffect(() => {
    if (contentType) fetchResults(contentType);
  }, [contentType, fetchResults]);

  const activeContentType = useMemo(
    () => contentTypes.find((c) => c.key === contentType) || null,
    [contentTypes, contentType]
  );

  const toggleModel = useCallback((key: string) => {
    setSelectedModels((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const commitNewWord = useCallback(() => {
    const w = newWord.trim();
    if (!w) return;
    addStagedWord(w);
    setNewWord('');
  }, [newWord, addStagedWord]);

  const cellCount = selectedModels.size * stagedWords.length;
  const canRun = !!contentType && selectedModels.size > 0 && stagedWords.length > 0 && !running;

  const handleRun = useCallback(async () => {
    setConfirming(false);
    try {
      await runBench(contentType, Array.from(selectedModels), stagedWords);
    } catch {
      // surfaced via store
    }
  }, [runBench, contentType, selectedModels, stagedWords]);

  // Registry models keyed by the display key, for chip labels.
  const modelLabel = useCallback(
    (key: string) => models.find((m) => modelKey(m) === key)?.displayName || key,
    [models]
  );

  const matrix = useMemo(() => buildMatrix(results, summary), [results, summary]);
  const hasAnyResults = results.length > 0;

  return (
    <div className="testbench-page">
      <div className="testbench-header">
        <h1 className="testbench-title">Model Test Bench</h1>
        <p className="testbench-subtitle">
          Runs the <strong>real pipeline stage prompt</strong> for the selected content type on each
          model &times; word. {summary.length} model{summary.length !== 1 ? 's' : ''} &middot; {total} run
          {total !== 1 ? 's' : ''} stored{activeContentType ? ` for ${activeContentType.label}` : ''}.
        </p>
      </div>

      {error && (
        <div className="testbench-error">
          <span>{error}</span>
          <button onClick={clearError}>Dismiss</button>
        </div>
      )}

      <div className="testbench-controls tb-controls-2col">
        {/* Words — free entry. Left column. */}
        <section className="tb-panel">
          <h2 className="tb-panel-title">Words <span className="tb-count">{stagedWords.length}</span></h2>
          {stagedWords.length > 0 && (
            <div className="tb-word-chips">
              {stagedWords.map((w) => (
                <span key={w} className="tb-word-chip">
                  {w}
                  <button className="tb-chip-remove" onClick={() => removeStagedWord(w)} title={`Remove ${w}`}>&times;</button>
                </span>
              ))}
            </div>
          )}
          <div className="tb-word-add">
            <input
              className="tb-input"
              type="text"
              value={newWord}
              placeholder="Type a word…"
              onChange={(e) => setNewWord(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commitNewWord(); } }}
            />
            <button className="tb-btn-secondary" onClick={commitNewWord} disabled={!newWord.trim()}>Add</button>
          </div>
          <p className="tb-panel-hint">The stage is fed only the word. Chained types generate Stage 1 from it first.</p>
        </section>

        {/* Models — collapsed accordion picker + chips. Right column. */}
        <section className="tb-panel">
          <h2 className="tb-panel-title">Models <span className="tb-count">{selectedModels.size}</span></h2>
          {selectedModels.size > 0 && (
            <div className="tb-word-chips">
              {[...selectedModels].map((key) => (
                <span key={key} className="tb-word-chip">
                  {modelLabel(key)}
                  <button className="tb-chip-remove" onClick={() => toggleModel(key)} title={`Remove ${modelLabel(key)}`}>&times;</button>
                </span>
              ))}
            </div>
          )}
          {loadingModels ? (
            <p className="tb-empty">Loading registry…</p>
          ) : models.length === 0 ? (
            <p className="tb-empty">No models registered.</p>
          ) : (
            <div className="tb-accordion">
              <button
                className="tb-accordion-toggle"
                aria-expanded={modelsOpen}
                onClick={() => setModelsOpen((v) => !v)}
              >
                <span>Add models</span>
                <span className="tb-accordion-caret">{modelsOpen ? '▾' : '▸'}</span>
              </button>
              {modelsOpen && (
                <div className="tb-model-list tb-accordion-list">
                  {models.map((m) => {
                    const key = modelKey(m);
                    const checked = selectedModels.has(key);
                    return (
                      <label key={key} className={`tb-model-item ${checked ? 'checked' : ''}`}>
                        <input type="checkbox" checked={checked} onChange={() => toggleModel(key)} />
                        <span className="tb-model-name">{m.displayName}</span>
                        <span className="tb-model-meta">{m.provider} &middot; {m.modelId}</span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </section>

        {/* Content type — full-width below, compact */}
        <section className="tb-panel tb-panel-full">
          <h2 className="tb-panel-title">Content Type</h2>
          <select
            className="tb-input"
            value={contentType}
            onChange={(e) => setContentType(e.target.value)}
          >
            {contentTypes.length === 0 && <option value="">Loading…</option>}
            {contentTypes.map((c) => (
              <option key={c.key} value={c.key}>{c.label}</option>
            ))}
          </select>
          {activeContentType && (
            <p className="tb-ct-meta">
              Stage <code>{activeContentType.stageId}</code> &middot; surfaces{' '}
              <code>{activeContentType.fields.join(', ')}</code>
              {' · '}
              {activeContentType.wordOnly
                ? <span className="tb-ct-flow">word-only</span>
                : <span className="tb-ct-flow">generates Stage 1 from the word first</span>}
            </p>
          )}
          {flaggedContentTypes.length > 0 && (
            <details className="tb-flagged">
              <summary>
                <span className="tb-flagged-icon" aria-hidden="true">ⓘ</span>
                {flaggedContentTypes.length} content types run inside a larger flow (not single-call testable)
              </summary>
              <ul>
                {flaggedContentTypes.map((f) => (
                  <li key={f.key}>
                    <strong>{f.label}</strong> ({f.stageId}): {f.reason}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </section>
      </div>

      <div className="tb-run-bar">
        <button className="tb-btn-run" onClick={() => setConfirming(true)} disabled={!canRun}>
          {running ? 'Running bench…' : 'Run Bench'}
        </button>
        {running && (
          <button className="tb-btn-stop" onClick={() => void stopRun()} title="Stop the run — remaining calls are cancelled; finished results are kept">
            Stop
          </button>
        )}
        <span className="tb-run-note">
          {cellCount > 0
            ? `${selectedModels.size} model${selectedModels.size !== 1 ? 's' : ''} × ${stagedWords.length} word${stagedWords.length !== 1 ? 's' : ''} = ${cellCount} paid call${cellCount !== 1 ? 's' : ''}`
            : 'Add a content type, at least one model, and at least one word.'}
        </span>
      </div>

      {running && runProgress && (
        <div className="tb-progress" role="status" aria-live="polite">
          <span className="tb-progress-spinner" aria-hidden="true" />
          <span className="tb-progress-count">
            {runProgress.done} of {runProgress.total} calls done
          </span>
          {runProgress.current && (
            <span className="tb-progress-current">— running: {runProgress.current}</span>
          )}
          <span className="tb-progress-bar">
            <span
              className="tb-progress-fill"
              style={{ width: `${runProgress.total ? Math.round((runProgress.done / runProgress.total) * 100) : 0}%` }}
            />
          </span>
        </div>
      )}

      <section className="tb-results">
        <h2 className="tb-panel-title">
          Cumulative Results{activeContentType ? ` — ${activeContentType.label}` : ''}
        </h2>

        {loadingResults && !hasAnyResults ? (
          <p className="tb-empty">Loading results…</p>
        ) : !hasAnyResults ? (
          <p className="tb-empty">No runs stored for this content type yet. Configure above, then run the bench.</p>
        ) : (
          <>
            {summary.length > 0 && (
              <div className="tb-summary">
                {summary.map((s) => {
                  const cost = costByModel[s.model];
                  const avg = cost && cost.count ? cost.total / cost.count : null;
                  return (
                    <div key={s.model} className="tb-summary-card">
                      <div className="tb-summary-model">{s.model}</div>
                      <div className="tb-summary-provider">{s.provider}</div>
                      <div className="tb-summary-stats">
                        <span className="tb-stat"><span className="tb-stat-num">{s.runs}</span><span className="tb-stat-label">runs</span></span>
                        <span className="tb-stat"><span className={`tb-stat-num ${okRateClass(s.okRate)}`}>{Math.round(s.okRate * 100)}%</span><span className="tb-stat-label">ok</span></span>
                      </div>
                      <div className="tb-summary-cost">
                        <span className="tb-cost-total">{cost ? fmtCost(cost.total) : '—'}</span>
                        <span className="tb-cost-avg">{avg != null ? `${fmtCost(avg)}/run` : 'no price'}</span>
                      </div>
                      <div className="tb-summary-ts">{s.lastTs ? formatTime(s.lastTs) : '—'}</div>
                    </div>
                  );
                })}
              </div>
            )}

            <div className="tb-matrix-wrap">
              <table className="tb-matrix">
                <thead>
                  <tr>
                    <th className="tb-matrix-corner">Model \ Word</th>
                    {matrix.words.map((w) => (
                      <th key={w} className="tb-matrix-word">{w}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {matrix.modelOrder.map((model) => (
                    <tr key={model}>
                      <th className="tb-matrix-model">{model}</th>
                      {matrix.words.map((w) => {
                        const cellId = `${model}::${w}`;
                        const res = matrix.cells[cellId];
                        return (
                          <td key={w} className="tb-matrix-cell">
                            {res ? (
                              <button
                                className={`tb-cell-btn ${res.ok ? 'ok' : res.cancelled ? 'cancelled' : 'fail'} ${expanded === cellId ? 'expanded' : ''}`}
                                onClick={() => setExpanded((prev) => (prev === cellId ? null : cellId))}
                                title={res.ok ? 'View output' : res.error || (res.cancelled ? 'Cancelled' : 'Failed')}
                              >
                                <span className="tb-cell-dot" />
                                <span className="tb-cell-preview">{cellPreview(res)}</span>
                              </button>
                            ) : (
                              <span className="tb-cell-empty">—</span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {expanded && matrix.cells[expanded] && (
              <ExpandedCell
                result={matrix.cells[expanded]}
                cost={runCost(matrix.cells[expanded], modelPricing)}
                onClose={() => setExpanded(null)}
              />
            )}
          </>
        )}
      </section>

      {confirming && (
        <div className="tb-confirm-overlay" onClick={() => setConfirming(false)}>
          <div className="tb-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="tb-confirm-title">Run bench — this spends API budget</h3>
            <p className="tb-confirm-body">
              This runs the real <strong>{activeContentType?.label}</strong> stage prompt as{' '}
              <strong>one live API call per model × word</strong>. With <strong>{selectedModels.size}</strong> model
              {selectedModels.size !== 1 ? 's' : ''} and <strong>{stagedWords.length}</strong> word
              {stagedWords.length !== 1 ? 's' : ''}, that is <strong>{cellCount}</strong> paid call
              {cellCount !== 1 ? 's' : ''}
              {activeContentType && !activeContentType.wordOnly && ', plus one Stage 1 generation per word'}. Real prompts are large.
              Web search is enabled for models that support it, matching production — search calls add cost. Continue?
            </p>
            <div className="tb-confirm-actions">
              <button className="tb-btn-secondary" onClick={() => setConfirming(false)}>Cancel</button>
              <button className="tb-btn-run tb-confirm-run" onClick={handleRun}>
                Run {cellCount} call{cellCount !== 1 ? 's' : ''}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function cellPreview(res: TestBenchResult): string {
  if (res.cancelled) return 'cancelled';
  if (!res.ok) return 'error';
  if (res.surfaced && Object.keys(res.surfaced).length) {
    const first = Object.values(res.surfaced)[0];
    const s = typeof first === 'string' ? first : JSON.stringify(first);
    return s.slice(0, 40) || '(empty)';
  }
  return (res.output || '').slice(0, 40) || '(empty)';
}

interface ExpandedCellProps {
  result: TestBenchResult;
  cost: number | null;
  onClose: () => void;
}

function ExpandedCell({ result, cost, onClose }: ExpandedCellProps) {
  return (
    <div className="tb-expanded">
      <div className="tb-expanded-header">
        <div className="tb-expanded-title">
          <span className="tb-expanded-model">{result.model}</span>
          <span className="tb-expanded-word">{result.word}</span>
          <span className={`tb-expanded-status ${result.ok ? 'ok' : 'fail'}`}>{result.ok ? 'ok' : 'error'}</span>
          {result.ok && <span className="tb-expanded-cost">{fmtCost(cost)}</span>}
          <span className="tb-expanded-ts">{formatTime(result.ts)}</span>
        </div>
        <button className="tb-expanded-close" onClick={onClose}>Close</button>
      </div>

      {result.ok && result.surfaced && Object.keys(result.surfaced).length > 0 && (
        <div className="tb-surfaced">
          <div className="tb-surfaced-label">Surfaced ({result.contentType})</div>
          {Object.entries(result.surfaced).map(([k, v]) => (
            <div key={k} className="tb-surfaced-field">
              <span className="tb-surfaced-key">{k}</span>
              <span className="tb-surfaced-val">{typeof v === 'string' ? v : JSON.stringify(v)}</span>
            </div>
          ))}
        </div>
      )}

      {result.ok ? (
        <details className="tb-raw">
          <summary>Full raw output</summary>
          <pre className="tb-expanded-output">{result.output || '(empty output)'}</pre>
        </details>
      ) : (
        <pre className="tb-expanded-output tb-expanded-error">{result.error || 'Unknown error'}</pre>
      )}

      {result.usage && Object.keys(result.usage).length > 0 && (
        <div className="tb-expanded-usage">
          {Object.entries(result.usage).map(([k, v]) => (
            <span key={k} className="tb-usage-item">
              <span className="tb-usage-key">{k}</span>
              <span className="tb-usage-val">{String(v)}</span>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

interface Matrix {
  modelOrder: string[];
  words: string[];
  cells: Record<string, TestBenchResult>;
}

// Build the model × word grid from the (content-type-scoped) results. Words and
// models are derived from the results themselves — most-recent result per cell.
function buildMatrix(results: TestBenchResult[], summary: TestBenchSummary[]): Matrix {
  const cells: Record<string, TestBenchResult> = {};
  const modelsSeen = new Set<string>();
  const wordsSeen = new Set<string>();

  for (const r of results) {
    modelsSeen.add(r.model);
    wordsSeen.add(r.word);
    const id = `${r.model}::${r.word}`;
    const existing = cells[id];
    if (!existing || new Date(r.ts).getTime() > new Date(existing.ts).getTime()) {
      cells[id] = r;
    }
  }

  const ordered: string[] = [];
  for (const s of summary) {
    if (modelsSeen.has(s.model)) {
      ordered.push(s.model);
      modelsSeen.delete(s.model);
    }
  }
  for (const m of modelsSeen) ordered.push(m);

  const words = [...wordsSeen].sort((a, b) => a.localeCompare(b));
  return { modelOrder: ordered, words, cells };
}

function okRateClass(rate: number): string {
  if (rate >= 0.9) return 'rate-good';
  if (rate >= 0.6) return 'rate-mid';
  return 'rate-bad';
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const diffMins = Math.floor((Date.now() - d.getTime()) / 60000);
  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${Math.floor(diffHours / 24)}d ago`;
}
